// ════════════════════════════════════════════════════════════════════════════
// DataTable — tabla del panel de administración.
//
// - Ordenar por columna (clic en la cabecera; vacíos siempre al final).
// - Columnas redimensionables (arrastrar el borde derecho de la cabecera;
//   doble clic = ancho por defecto) y ocultables. Anchos y columnas visibles se
//   guardan por navegador en localStorage (`storageKey`).
// - Cabecera fija, paginación compacta, filas por página y exportar CSV de las
//   filas filtradas (todas, no solo la página visible).
//
// El filtrado lo hace quien la usa: DataTable recibe ya las filas filtradas.
//
// columns: [{ key, label, width, minWidth?, sortable?, sortValue?(row), render?(row),
//             csv?(row), title?(row), align?, hideable? (def. true), defaultHidden? }]
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Columns3, Download, RotateCcw } from 'lucide-react';

const PAGE_SIZES = [20, 50, 100];

function loadPrefs(key) {
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
}
function savePrefs(key, prefs) {
  try { localStorage.setItem(key, JSON.stringify(prefs)); } catch { /* modo privado: no pasa nada */ }
}

function compare(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
}

function csvCell(v) {
  const s = v == null ? '' : String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Páginas a mostrar: 1 … 4 5 [6] 7 8 … 30 */
function pageList(current, total) {
  const set = new Set([0, total - 1, current - 2, current - 1, current, current + 1, current + 2]);
  const pages = [...set].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
  const out = [];
  pages.forEach((p, i) => { if (i && p - pages[i - 1] > 1) out.push('…' + p); out.push(p); });
  return out;
}

export default function DataTable({
  columns, rows, rowKey, storageKey, defaultSort = { key: null, dir: 'desc' },
  renderExpanded, expandedKey, toolbar, csvName = 'export', emptyText = 'Sin resultados.',
  testId,
}) {
  const [prefs, setPrefs] = useState(() => loadPrefs(storageKey));
  const [sort, setSort] = useState(() => prefs.sort || defaultSort);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => (PAGE_SIZES.includes(prefs.pageSize) ? prefs.pageSize : 20));
  const [showCols, setShowCols] = useState(false);
  const colsMenuRef = useRef(null);

  useEffect(() => { savePrefs(storageKey, { ...prefs, sort, pageSize }); }, [storageKey, prefs, sort, pageSize]);

  // Volver a la primera página cuando cambia el filtro (cambia el nº de filas)
  useEffect(() => { setPage(0); }, [rows.length]);

  useEffect(() => {
    if (!showCols) return undefined;
    const close = (e) => { if (colsMenuRef.current && !colsMenuRef.current.contains(e.target)) setShowCols(false); };
    const onKey = (e) => { if (e.key === 'Escape') setShowCols(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', onKey); };
  }, [showCols]);

  const hidden = prefs.hidden ?? columns.filter((c) => c.defaultHidden).map((c) => c.key);
  const visible = columns.filter((c) => !hidden.includes(c.key));
  const widthOf = (c) => prefs.widths?.[c.key] ?? c.width ?? 120;
  const totalWidth = visible.reduce((s, c) => s + widthOf(c), 0);

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const val = col.sortValue || ((r) => r[col.key]);
    const empty = (v) => v == null || v === '';
    return [...rows].sort((ra, rb) => {
      const a = val(ra); const b = val(rb);
      if (empty(a) && empty(b)) return 0;
      if (empty(a)) return 1;
      if (empty(b)) return -1;
      return sort.dir === 'asc' ? compare(a, b) : compare(b, a);
    });
  }, [rows, columns, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const toggleSort = (c) => {
    if (c.sortable === false) return;
    setSort((s) => (s.key === c.key ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: 'desc' }));
    setPage(0);
  };

  const toggleColumn = (key) => {
    const next = hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key];
    if (columns.length - next.length < 1) return;
    setPrefs((p) => ({ ...p, hidden: next }));
  };

  const startResize = (e, c) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthOf(c);
    const min = c.minWidth ?? 60;
    const onMove = (ev) => {
      const w = Math.max(min, Math.round(startW + ev.clientX - startX));
      setPrefs((p) => ({ ...p, widths: { ...(p.widths || {}), [c.key]: w } }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const resetWidth = (c) => setPrefs((p) => {
    const widths = { ...(p.widths || {}) };
    delete widths[c.key];
    return { ...p, widths };
  });

  const exportCsv = () => {
    const cols = visible.filter((c) => c.csv !== false);
    const head = cols.map((c) => csvCell(c.label)).join(';');
    const body = sorted.map((r) => cols.map((c) => csvCell(c.csv ? c.csv(r) : r[c.key])).join(';'));
    // BOM + ';' para que Excel en español lo abra con acentos y columnas bien
    const blob = new Blob(['﻿' + [head, ...body].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${csvName}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="adm-dt" data-testid={testId}>
      <div className="adm-dt-bar">
        <div className="adm-dt-bar-l">{toolbar}</div>
        <div className="adm-dt-bar-r">
          <label className="adm-dt-pagesize">
            Filas
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <div className="adm-dt-cols" ref={colsMenuRef}>
            <button type="button" className="adm-btn" onClick={() => setShowCols((v) => !v)} aria-expanded={showCols}>
              <Columns3 size={14} /> Columnas
            </button>
            {showCols && (
              <div className="adm-dt-cols-menu" role="menu">
                {columns.filter((c) => c.hideable !== false).map((c) => (
                  <label key={c.key}>
                    <input type="checkbox" checked={!hidden.includes(c.key)} onChange={() => toggleColumn(c.key)} />
                    {c.label}
                  </label>
                ))}
                <button type="button" className="adm-btn adm-btn--ghost" onClick={() => { setPrefs({}); setShowCols(false); }}>
                  <RotateCcw size={13} /> Restablecer columnas y anchos
                </button>
              </div>
            )}
          </div>
          <button type="button" className="adm-btn" onClick={exportCsv} disabled={!sorted.length} title="Exporta las filas filtradas">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      <div className="adm-dt-wrap">
        <table className="adm-dt-table" style={{ width: totalWidth, minWidth: '100%' }}>
          <colgroup>
            {visible.map((c) => <col key={c.key} style={{ width: widthOf(c) }} />)}
          </colgroup>
          <thead>
            <tr>
              {visible.map((c) => (
                <th
                  key={c.key}
                  className={c.sortable === false ? '' : 'adm-dt-sortable'}
                  style={{ textAlign: c.align || 'left' }}
                  onClick={() => toggleSort(c)}
                  aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <span className="adm-dt-th-label">{c.label}</span>
                  {sort.key === c.key && <span className="adm-dt-sort">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                  <span
                    className="adm-dt-resize"
                    role="separator"
                    aria-orientation="vertical"
                    title="Arrastra para cambiar el ancho · doble clic para restablecer"
                    onPointerDown={(e) => startResize(e, c)}
                    onDoubleClick={(e) => { e.stopPropagation(); resetWidth(c); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const k = rowKey(r);
              return (
                <React.Fragment key={k}>
                  <tr>
                    {visible.map((c) => (
                      <td key={c.key} style={{ textAlign: c.align || 'left' }} title={c.title ? c.title(r) : undefined}>
                        {c.render ? c.render(r) : (r[c.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                  {renderExpanded && expandedKey === k && (
                    <tr className="adm-dt-expanded">
                      <td colSpan={visible.length}>{renderExpanded(r)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!pageRows.length && (
              <tr><td colSpan={visible.length} className="adm-dt-empty">{emptyText}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="adm-dt-foot">
        <span className="adm-dt-count">
          {sorted.length ? `${safePage * pageSize + 1}–${Math.min((safePage + 1) * pageSize, sorted.length)} de ${sorted.length}` : '0 resultados'}
        </span>
        {totalPages > 1 && (
          <div className="admin-users-pagination" style={{ padding: 0 }}>
            <button type="button" className="admin-page-btn" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} aria-label="Página anterior">‹</button>
            {pageList(safePage, totalPages).map((p) => (typeof p === 'string'
              ? <span key={p} className="adm-dt-ellipsis">…</span>
              : <button type="button" key={p} className={`admin-page-btn${p === safePage ? ' admin-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p + 1}</button>))}
            <button type="button" className="admin-page-btn" disabled={safePage === totalPages - 1} onClick={() => setPage(safePage + 1)} aria-label="Página siguiente">›</button>
          </div>
        )}
      </div>
    </div>
  );
}
